import React, { useState } from 'react'
import { CheckIcon, EnvelopeIcon, ExclamationCircleIcon, ExclamationTriangleIcon, PencilIcon } from '@heroicons/react/24/outline'
import outlookIcon from '../../media/outlook.svg'
import gmailIcon from '../../media/google.svg'
import { useDarkMode } from '../../context/DarkModeContext'
import SignatureModal from './SignatureModal'
import ActionsMenu from './ActionsMenu'

const PROVIDER_LABELS = {
	gmail: 'Gmail',
	outlook: 'Outlook',
	zoho: 'Zoho Mail',
	yahoo: 'Yahoo Mail',
	godaddy: 'GoDaddy',
	smtp: 'SMTP',
}
const PROVIDER_ICONS = { gmail: gmailIcon, outlook: outlookIcon }
const OAUTH_PROVIDERS = new Set(['gmail', 'outlook'])
// Every card uses the same columns from sm up, so the chips and signatures line up from card to card.
const CARD_COLUMNS = 'sm:grid sm:grid-cols-[minmax(0,1fr)_17rem_12rem_2.25rem] sm:items-center sm:gap-5'
const CHIP = 'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[13px] font-medium whitespace-nowrap'

const addedDate = account =>
	account.createdAt ? new Date(account.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null

function ProviderTile({ provider, isLight }) {
	const icon = PROVIDER_ICONS[provider]
	return (
		<div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${isLight ? 'bg-slate-100' : 'bg-white/5'}`}>
			{icon ? (
				<img src={icon} alt="" className="w-5 h-5" />
			) : (
				<EnvelopeIcon className={`w-5 h-5 ${isLight ? 'text-slate-500' : 'text-gray-400'}`} />
			)}
		</div>
	)
}

function OnChip({ label, isLight }) {
	return (
		<span className={`${CHIP} ${isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/10 text-emerald-400'}`}>
			<CheckIcon className="w-3.5 h-3.5 stroke-[2.6]" />
			{label}
		</span>
	)
}

function ProblemChip({ label, isLight }) {
	return (
		<span className={`${CHIP} ${isLight ? 'bg-red-50 text-red-700' : 'bg-red-500/10 text-red-400'}`}>
			<ExclamationCircleIcon className="w-4 h-4" />
			{label}
		</span>
	)
}

function EnableReadButton({ onClick, isLight }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`${CHIP} ${
				isLight ? 'bg-amber-50 text-amber-800 hover:bg-amber-100' : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
			}`}
		>
			<ExclamationTriangleIcon className="w-4 h-4" />
			Enable read
		</button>
	)
}

function SignatureButton({ name, onClick, isLight }) {
	const tone = name
		? isLight ? 'bg-slate-50 text-slate-900 hover:bg-slate-100' : 'bg-white/5 text-white hover:bg-white/10'
		: isLight ? 'text-slate-500 hover:bg-slate-50 hover:text-slate-900' : 'text-gray-400 hover:bg-white/5 hover:text-white'
	return (
		<button
			type="button"
			onClick={onClick}
			title={name ? 'Edit signature' : undefined}
			className={`${CHIP} max-w-full min-w-0 ${tone}`}
		>
			<PencilIcon className={`w-3.5 h-3.5 shrink-0 ${isLight ? 'text-slate-500' : 'text-gray-400'}`} />
			<span className="truncate">{name || 'Add signature'}</span>
		</button>
	)
}

const EmailAccountList = ({ accounts, onReAuthorization, canEnableRead = false, onDeleteAccount, onSaveSignature, onRemoveSignature }) => {
	const { isDarkMode } = useDarkMode()
	const isLight = !isDarkMode
	const [deletingAccountId, setDeletingAccountId] = useState(null)
	const [signatureAccount, setSignatureAccount] = useState(null)
	const [error, setError] = useState(null)

	const handleDeleteAccount = async account => {
		if (!window.confirm(`Remove ${account.email} from LoadFetcher?`)) return
		setDeletingAccountId(account._id)
		setError(null)
		try {
			await onDeleteAccount(account._id)
		} catch (err) {
			setError(err.message || 'Failed to remove the account')
		} finally {
			setDeletingAccountId(null)
		}
	}

	// Returns whether the signature was deleted, so the popup knows to close.
	const handleDeleteSignature = async account => {
		if (!window.confirm(`Delete the signature for ${account.email}?`)) return false
		await onRemoveSignature(account._id)
		return true
	}

	return (
		<>
			{error && (
				<div className={`px-4 py-3 rounded-lg mb-3 text-sm ${isLight ? 'bg-red-500/10 text-red-600' : 'bg-red-500/10 text-red-400'}`}>
					{error}
				</div>
			)}

			<div className="flex flex-col gap-2.5">
				{accounts.map(account => {
					// Expired sign-in or send unticked on the provider's screen: both are fixed by re-authorizing.
					const needsReauth = !account.canSendEmail && OAUTH_PROVIDERS.has(account.provider)
					// Reading is Gmail-only and, until Google approves it, hidden from everyone but admins.
					const showsRead = canEnableRead && account.provider === 'gmail'
					const added = addedDate(account)
					const actions = [{ label: 'Remove mailbox', onClick: () => handleDeleteAccount(account), danger: true }]
					const menu = <ActionsMenu label="Mailbox actions" actions={actions} />

					const sendChip = account.canSendEmail ? (
						<OnChip label="Send enabled" isLight={isLight} />
					) : (
						<ProblemChip label="Send disabled" isLight={isLight} />
					)
					// With sending off, Re-authorize comes first, so read shows as disabled rather than offering Enable.
					const readChip = !showsRead ? null : account.canReadEmail ? (
						<OnChip label="Read enabled" isLight={isLight} />
					) : account.canSendEmail ? (
						<EnableReadButton onClick={() => onReAuthorization(account.email, { readAccess: true })} isLight={isLight} />
					) : (
						<ProblemChip label="Read disabled" isLight={isLight} />
					)
					const status = (
						<>
							{sendChip}
							{readChip}
						</>
					)

					const secondary = needsReauth ? (
						<button
							type="button"
							onClick={() => onReAuthorization(account.email, { readAccess: showsRead })}
							className="h-8 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[13px] font-medium"
						>
							Re-authorize
						</button>
					) : (
						<SignatureButton
							name={account.signature?.signatureData?.name}
							onClick={() => setSignatureAccount(account)}
							isLight={isLight}
						/>
					)

					return (
						<div
							key={account._id}
							className={`p-3.5 sm:py-4 sm:pl-5 sm:pr-4 rounded-2xl border shadow-sm ${CARD_COLUMNS} ${
								needsReauth
									? isLight ? 'border-red-200' : 'border-red-500/40'
									: isLight ? 'border-slate-200' : 'border-[#374151]'
							} ${isLight ? 'bg-white' : 'bg-white/[0.02]'} ${deletingAccountId === account._id ? 'opacity-50 pointer-events-none' : ''}`}
						>
							<div className="flex items-center gap-3 min-w-0">
								<ProviderTile provider={account.provider} isLight={isLight} />
								<div className="min-w-0 flex-1">
									<div
										className={`text-[15px] font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`}
										title={account.email}
									>
										{account.email}
									</div>
									<div className={`text-xs mt-0.5 truncate ${isLight ? 'text-slate-500' : 'text-gray-400'}`}>
										{PROVIDER_LABELS[account.provider] || account.provider}
										{added && ` · since ${added}`}
									</div>
								</div>
								<div className="sm:hidden">{menu}</div>
							</div>

							{/* Phone: chips and signature share one wrapping row under the mailbox. */}
							<div className="mt-3 flex flex-wrap items-center gap-1.5 sm:contents">
								<div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 sm:min-w-0">{status}</div>
								<div className="min-w-0 max-w-full">{secondary}</div>
							</div>

							<div className="hidden sm:block">{menu}</div>
						</div>
					)
				})}
			</div>

			{signatureAccount && (
				<SignatureModal
					account={signatureAccount}
					onSave={onSaveSignature}
					onDelete={signatureAccount.signature ? handleDeleteSignature : null}
					onClose={() => setSignatureAccount(null)}
				/>
			)}
		</>
	)
}

export default EmailAccountList
